/**
 * Hala Control Center - Leads Management
 * Premium CRM-ready leads management interface
 */

import { useState, useEffect } from 'react';
import { 
  Search, 
  Plus,
  Phone,
  Mail,
  MapPin,
  User,
  Calendar,
  MessageSquare,
  Edit2,
  X,
  CheckCircle,
  Clock,
  UserCheck,
  Download
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/control-center`;

const COLORS = {
  primary: '#1B3A6B',
  gold: '#C5A572',
  success: '#1D8F4E',
  warning: '#F59E0B',
  error: '#EF4444',
  white: '#FFFFFF',
  bg: '#F8FAFC',
  bgDark: '#F1F5F9',
  border: '#E2E8F0',
  text: '#1E293B',
  textMuted: '#64748B',
  textLight: '#94A3B8',
};

const STATUS_CONFIG = {
  new: { label: 'جديد', color: COLORS.primary },
  pending_assignment: { label: 'في انتظار التعيين', color: COLORS.warning },
  in_progress: { label: 'قيد المتابعة', color: COLORS.gold },
  contacted: { label: 'تم التواصل', color: COLORS.success },
  interested: { label: 'مهتم', color: COLORS.success },
  not_interested: { label: 'غير مهتم', color: COLORS.error },
  closed: { label: 'مغلق', color: COLORS.textLight }
};

const CITIES = ['الرياض', 'جدة', 'الدمام', 'مكة', 'المدينة', 'الباحة', 'أبها', 'تبوك'];

// Lead Card
function LeadCard({ lead, onClick }) {
  const status = STATUS_CONFIG[lead.status] || STATUS_CONFIG.new;
  
  return (
    <div 
      onClick={onClick}
      style={{
        backgroundColor: COLORS.white,
        borderRadius: '12px',
        border: `1px solid ${COLORS.border}`,
        padding: '20px',
        cursor: 'pointer',
        transition: 'all 0.2s',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
      }}
      onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)'}
      onMouseLeave={e => e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)'}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '44px',
            height: '44px',
            borderRadius: '10px',
            backgroundColor: `${COLORS.primary}15`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <User size={22} color={COLORS.primary} />
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: COLORS.text }}>{lead.customer_name}</h3>
            <p style={{ margin: '2px 0 0', fontSize: '12px', color: COLORS.textMuted }}>{lead.user_type || 'غير محدد'}</p>
          </div>
        </div>
        <span style={{
          padding: '4px 10px',
          borderRadius: '6px',
          backgroundColor: `${status.color}15`,
          color: status.color,
          fontSize: '11px',
          fontWeight: 500
        }}>
          {status.label}
        </span>
      </div>
      
      {/* Contact Info */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
        {lead.mobile && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Phone size={14} color={COLORS.textMuted} />
            <span style={{ fontSize: '13px', color: COLORS.text, direction: 'ltr' }}>{lead.mobile}</span>
          </div>
        )}
        {lead.email && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Mail size={14} color={COLORS.textMuted} />
            <span style={{ fontSize: '13px', color: COLORS.text }}>{lead.email}</span>
          </div>
        )}
        {lead.city && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <MapPin size={14} color={COLORS.textMuted} />
            <span style={{ fontSize: '13px', color: COLORS.text }}>{lead.city}</span>
          </div>
        )}
      </div>
      
      {/* Footer */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        paddingTop: '12px',
        borderTop: `1px solid ${COLORS.border}`
      }}>
        <span style={{ fontSize: '12px', color: COLORS.textLight }}>
          {lead.created_at ? new Date(lead.created_at).toLocaleDateString('ar-SA') : '-'}
        </span>
        {lead.assigned_to && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <UserCheck size={14} color={COLORS.success} />
            <span style={{ fontSize: '12px', color: COLORS.textMuted }}>معيّن</span>
          </div>
        )}
      </div>
    </div>
  );
}

// Lead Detail Modal
function LeadModal({ lead, isOpen, onClose, onUpdate }) {
  const [newNote, setNewNote] = useState('');
  const [selectedStatus, setSelectedStatus] = useState(lead?.status || 'new');
  
  if (!isOpen || !lead) return null;
  
  const handleStatusChange = async () => {
    try {
      await fetch(`${API}/leads/${lead.lead_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: selectedStatus })
      });
      onUpdate();
    } catch (e) {
      console.error('Failed to update lead:', e);
    }
  };
  
  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    
    try {
      await fetch(`${API}/leads/${lead.lead_id}/notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          author_id: 'user_admin',
          author_name: 'مدير النظام',
          content: newNote
        })
      });
      setNewNote('');
      onUpdate();
    } catch (e) {
      console.error('Failed to add note:', e);
    }
  };
  
  const status = STATUS_CONFIG[lead.status] || STATUS_CONFIG.new;
  
  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }} onClick={onClose}>
      <div 
        style={{
          backgroundColor: COLORS.white,
          borderRadius: '16px',
          width: '100%',
          maxWidth: '600px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 25px 50px rgba(0,0,0,0.25)'
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ 
          padding: '20px 24px', 
          borderBottom: `1px solid ${COLORS.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: '12px',
              backgroundColor: `${COLORS.primary}15`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <User size={24} color={COLORS.primary} />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: COLORS.text }}>{lead.customer_name}</h2>
              <span style={{
                display: 'inline-block',
                marginTop: '4px',
                padding: '2px 8px',
                borderRadius: '4px',
                backgroundColor: `${status.color}15`,
                color: status.color,
                fontSize: '11px',
                fontWeight: 500
              }}>
                {status.label}
              </span>
            </div>
          </div>
          <button onClick={onClose} style={{
            width: '36px',
            height: '36px',
            borderRadius: '8px',
            border: 'none',
            backgroundColor: COLORS.bg,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <X size={18} color={COLORS.textMuted} />
          </button>
        </div>
        
        {/* Content */}
        <div style={{ padding: '24px', overflowY: 'auto', flex: 1 }}>
          {/* Contact Info */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr 1fr', 
            gap: '16px',
            marginBottom: '24px'
          }}>
            <div>
              <p style={{ fontSize: '12px', color: COLORS.textMuted, margin: '0 0 4px' }}>رقم الجوال</p>
              <p style={{ fontSize: '14px', fontWeight: 500, color: COLORS.text, margin: 0, direction: 'ltr', textAlign: 'right' }}>
                {lead.mobile || '-'}
              </p>
            </div>
            <div>
              <p style={{ fontSize: '12px', color: COLORS.textMuted, margin: '0 0 4px' }}>البريد الإلكتروني</p>
              <p style={{ fontSize: '14px', fontWeight: 500, color: COLORS.text, margin: 0 }}>
                {lead.email || '-'}
              </p>
            </div>
            <div>
              <p style={{ fontSize: '12px', color: COLORS.textMuted, margin: '0 0 4px' }}>المدينة</p>
              <p style={{ fontSize: '14px', fontWeight: 500, color: COLORS.text, margin: 0 }}>
                {lead.city || '-'}
              </p>
            </div>
            <div>
              <p style={{ fontSize: '12px', color: COLORS.textMuted, margin: '0 0 4px' }}>نوع المستخدم</p>
              <p style={{ fontSize: '14px', fontWeight: 500, color: COLORS.text, margin: 0 }}>
                {lead.user_type || '-'}
              </p>
            </div>
            <div>
              <p style={{ fontSize: '12px', color: COLORS.textMuted, margin: '0 0 4px' }}>الاحتياج</p>
              <p style={{ fontSize: '14px', fontWeight: 500, color: COLORS.text, margin: 0 }}>
                {lead.need || '-'}
              </p>
            </div>
            <div>
              <p style={{ fontSize: '12px', color: COLORS.textMuted, margin: '0 0 4px' }}>الخدمة المختارة</p>
              <p style={{ fontSize: '14px', fontWeight: 500, color: COLORS.text, margin: 0 }}>
                {lead.selected_service || '-'}
              </p>
            </div>
          </div>
          
          {/* Status Update */}
          <div style={{ marginBottom: '24px' }}>
            <p style={{ fontSize: '13px', fontWeight: 500, color: COLORS.text, margin: '0 0 8px' }}>تحديث الحالة</p>
            <div style={{ display: 'flex', gap: '8px' }}>
              <select
                value={selectedStatus}
                onChange={e => setSelectedStatus(e.target.value)}
                style={{
                  flex: 1,
                  padding: '10px 14px',
                  borderRadius: '8px',
                  border: `1px solid ${COLORS.border}`,
                  fontSize: '13px',
                  fontFamily: 'inherit',
                  outline: 'none'
                }}
              >
                {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                  <option key={key} value={key}>{config.label}</option>
                ))}
              </select>
              <button
                onClick={handleStatusChange}
                disabled={selectedStatus === lead.status}
                style={{
                  padding: '10px 16px',
                  borderRadius: '8px',
                  border: 'none',
                  backgroundColor: selectedStatus !== lead.status ? COLORS.primary : COLORS.bgDark,
                  color: selectedStatus !== lead.status ? COLORS.white : COLORS.textLight,
                  fontSize: '13px',
                  cursor: selectedStatus !== lead.status ? 'pointer' : 'not-allowed'
                }}
              >
                حفظ
              </button>
            </div>
          </div>
          
          {/* Notes */}
          <div>
            <p style={{ fontSize: '13px', fontWeight: 500, color: COLORS.text, margin: '0 0 12px' }}>الملاحظات</p>
            
            {/* Add Note */}
            <div style={{ marginBottom: '16px' }}>
              <textarea
                value={newNote}
                onChange={e => setNewNote(e.target.value)}
                placeholder="أضف ملاحظة..."
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: `1px solid ${COLORS.border}`,
                  fontSize: '13px',
                  fontFamily: 'inherit',
                  resize: 'vertical',
                  minHeight: '80px',
                  outline: 'none'
                }}
              />
              <button
                onClick={handleAddNote}
                disabled={!newNote.trim()}
                style={{
                  marginTop: '8px',
                  padding: '10px 16px',
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: newNote.trim() ? COLORS.primary : COLORS.bgDark,
                  color: newNote.trim() ? COLORS.white : COLORS.textLight,
                  fontSize: '13px',
                  cursor: newNote.trim() ? 'pointer' : 'not-allowed'
                }}
              >
                إضافة ملاحظة
              </button>
            </div>
            
            {/* Notes List */}
            {(!lead.notes || lead.notes.length === 0) ? (
              <div style={{ textAlign: 'center', padding: '24px', color: COLORS.textMuted }}>
                <MessageSquare size={24} style={{ marginBottom: '8px', opacity: 0.5 }} />
                <p style={{ margin: 0, fontSize: '13px' }}>لا توجد ملاحظات</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {lead.notes.map((note, i) => (
                  <div key={i} style={{
                    padding: '14px',
                    backgroundColor: COLORS.bg,
                    borderRadius: '8px'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span style={{ fontSize: '13px', fontWeight: 500, color: COLORS.text }}>{note.author_name}</span>
                      <span style={{ fontSize: '11px', color: COLORS.textLight }}>
                        {note.created_at ? new Date(note.created_at).toLocaleDateString('ar-SA') : ''}
                      </span>
                    </div>
                    <p style={{ margin: 0, fontSize: '13px', color: COLORS.textMuted, lineHeight: 1.6 }}>{note.content}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function LeadsPage() {
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [selectedLead, setSelectedLead] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchLeads();
  }, [searchQuery, statusFilter, cityFilter]);

  const fetchLeads = async () => {
    setLoading(true);
    try {
      let url = `${API}/leads?limit=100`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
      if (statusFilter) url += `&status=${statusFilter}`;
      if (cityFilter) url += `&city=${encodeURIComponent(cityFilter)}`;
      
      const res = await fetch(url);
      const data = await res.json();
      setLeads(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      console.error('Failed to fetch leads:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleLeadClick = async (lead) => {
    try {
      const res = await fetch(`${API}/leads/${lead.lead_id}`);
      const data = await res.json();
      setSelectedLead(data);
      setShowModal(true);
    } catch (e) {
      console.error('Failed to fetch lead details:', e);
    }
  };

  // Group leads by status
  const groupedLeads = {
    new: leads.filter(l => l.status === 'new'),
    pending_assignment: leads.filter(l => l.status === 'pending_assignment'),
    in_progress: leads.filter(l => l.status === 'in_progress'),
    contacted: leads.filter(l => l.status === 'contacted'),
    other: leads.filter(l => !['new', 'pending_assignment', 'in_progress', 'contacted'].includes(l.status))
  };

  const selectStyle = {
    padding: '10px 14px',
    borderRadius: '8px',
    border: `1px solid ${COLORS.border}`,
    backgroundColor: COLORS.white,
    fontSize: '13px',
    fontFamily: 'inherit',
    outline: 'none',
    cursor: 'pointer',
    minWidth: '140px'
  };

  return (
    <div>
      {/* Page Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: COLORS.text, margin: '0 0 8px' }}>العملاء المحتملون</h1>
          <p style={{ fontSize: '14px', color: COLORS.textMuted, margin: 0 }}>إدارة ومتابعة العملاء المحتملين</p>
        </div>
        <button style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '10px 16px',
          borderRadius: '8px',
          border: 'none',
          backgroundColor: COLORS.bg,
          color: COLORS.text,
          fontSize: '13px',
          cursor: 'pointer'
        }}>
          <Download size={16} />
          تصدير
        </button>
      </div>

      {/* Stats */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
        gap: '16px',
        marginBottom: '24px'
      }}>
        {Object.entries({
          new: 'جديد',
          pending_assignment: 'انتظار التعيين',
          in_progress: 'قيد المتابعة',
          contacted: 'تم التواصل'
        }).map(([key, label]) => {
          const count = groupedLeads[key]?.length || 0;
          const status = STATUS_CONFIG[key];
          return (
            <div key={key} style={{
              backgroundColor: COLORS.white,
              borderRadius: '12px',
              padding: '16px 20px',
              border: `1px solid ${COLORS.border}`
            }}>
              <p style={{ fontSize: '24px', fontWeight: 'bold', color: status.color, margin: '0 0 4px' }}>{count}</p>
              <p style={{ fontSize: '13px', color: COLORS.textMuted, margin: 0 }}>{label}</p>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div style={{
        backgroundColor: COLORS.white,
        borderRadius: '12px',
        padding: '16px 20px',
        marginBottom: '20px',
        border: `1px solid ${COLORS.border}`,
        display: 'flex',
        gap: '12px',
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          backgroundColor: COLORS.bg,
          padding: '10px 14px',
          borderRadius: '8px',
          flex: 1,
          minWidth: '250px'
        }}>
          <Search size={16} color={COLORS.textMuted} />
          <input
            type="text"
            placeholder="بحث بالاسم أو رقم الجوال أو البريد..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            style={{
              border: 'none',
              backgroundColor: 'transparent',
              outline: 'none',
              flex: 1,
              fontSize: '13px',
              fontFamily: 'inherit'
            }}
          />
        </div>
        
        <select 
          value={statusFilter} 
          onChange={e => setStatusFilter(e.target.value)}
          style={selectStyle}
        >
          <option value="">كل الحالات</option>
          {Object.entries(STATUS_CONFIG).map(([key, config]) => (
            <option key={key} value={key}>{config.label}</option>
          ))}
        </select>
        
        <select 
          value={cityFilter} 
          onChange={e => setCityFilter(e.target.value)}
          style={selectStyle}
        >
          <option value="">كل المدن</option>
          {CITIES.map(city => (
            <option key={city} value={city}>{city}</option>
          ))}
        </select>
      </div>

      {/* Leads Grid */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px', color: COLORS.textMuted }}>
          جاري التحميل...
        </div>
      ) : leads.length === 0 ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '60px',
          backgroundColor: COLORS.white,
          borderRadius: '16px',
          border: `1px solid ${COLORS.border}`
        }}>
          <User size={48} color={COLORS.textLight} style={{ marginBottom: '16px' }} />
          <p style={{ color: COLORS.textMuted, margin: 0 }}>لا يوجد عملاء محتملون</p>
        </div>
      ) : (
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', 
          gap: '16px' 
        }}>
          {leads.map(lead => (
            <LeadCard
              key={lead.lead_id}
              lead={lead}
              onClick={() => handleLeadClick(lead)}
            />
          ))}
        </div>
      )}

      {/* Lead Modal */}
      <LeadModal
        lead={selectedLead}
        isOpen={showModal}
        onClose={() => { setShowModal(false); setSelectedLead(null); }}
        onUpdate={() => { fetchLeads(); if (selectedLead) handleLeadClick(selectedLead); }}
      />
    </div>
  );
}
